import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { HelpTextDialogService } from 'src/app/service/help-text-dialog.service';

@Component({
    selector: 'app-school-year',
    templateUrl: './school-year.component.html',
    styleUrls: ['./school-year.component.scss']
})
export class SchoolYearComponent implements OnInit {
    years: string[] = [];
    selected: string;

    @Input() selectSchoolYear: string = '';
    @Input() label: string = '';
    @Input() yearType: 'SchoolYear' | 'PerformancePeriod' = 'SchoolYear';
    @Input() yearsBack: number = 4;
    @Input() yearsForward: number = 1;
    @Input() currentYearModifier: number = 0;
    @Input() minYear: number;
    @Input() startingSchoolYearDate: Date;
    @Input() disabled: boolean = false;
    @Input() required: boolean = false;
    @Input('reset') reset: boolean;
    @Output() performancePeriodEvent = new EventEmitter<string>();
    @Output() populateSchoolYearsEvent = new EventEmitter<string[]>();

    performancePeriod: UntypedFormGroup;
    initialYear: string = '';

    constructor(
        private fb: UntypedFormBuilder,
        public helpTextDialogService: HelpTextDialogService
    ) {
        this.performancePeriod = this.fb.group({
            performanceSelect: ['']
        });
    }

    ngOnInit(): void {
        switch (this.yearType) {
            case 'PerformancePeriod':
                this.years = this.populatePerformancePeriods(this.yearsBack, this.yearsForward);
                this.populateSchoolYearsEvent.emit(this.years);
                break;
            case 'SchoolYear':
            default:
                this.years = this.populateSchoolYears(this.yearsBack, this.yearsForward);
                this.populateSchoolYearsEvent.emit(this.years);
                break;
        }

        this.performanceSelect.valueChanges.subscribe((schoolYear: string) => {
            this.performancePeriodEvent.emit(schoolYear);
        });
    }

    get performanceSelect(): AbstractControl {
        return this.performancePeriod.get('performanceSelect')!;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.reset) {
            this.selectSchoolYear = '';
            this.selectInitialYear();
        }

        if (this.disabled) {
            this.performancePeriod.disable();
        } else {
            this.performancePeriod.enable();
        }

        if (changes['selectSchoolYear']) {
            let found = this.years.findIndex((year) => year === changes['selectSchoolYear'].currentValue);
            if (found >= 0) {
                this.performanceSelect.setValue(this.years[found]);
                this.performancePeriodEvent.emit(this.years[found]);
            } else {
                this.years.push(changes['selectSchoolYear'].currentValue);
                this.performanceSelect.setValue(changes['selectSchoolYear'].currentValue);
                this.performancePeriodEvent.emit(changes['selectSchoolYear'].currentValue);
            }
        }
    }

    populatePerformancePeriods(yearsBack: number, yearsForward: number): string[] {
        const today = new Date();
        const currentMonth: number = today.getMonth() + 1;
        let currentYear: number;

        if (this.startingSchoolYearDate) {
            if (today >= this.startingSchoolYearDate) {
                currentYear = this.startingSchoolYearDate.getFullYear() + this.currentYearModifier;
            } else {
                currentYear = this.startingSchoolYearDate.getFullYear() - 1 + this.currentYearModifier;
            }
        } else if (currentMonth >= 9) {
            currentYear = today.getFullYear() + this.currentYearModifier;
        } else {
            currentYear = today.getFullYear() - 1 + this.currentYearModifier;
        }

        if (this.minYear) {
            yearsBack = currentYear - this.minYear;
        }

        let output: string[] = [];

        // Populate yearsBack
        for (let i = yearsBack; i > 0; i--) {
            output.push(this.yearReducer(currentYear - i));
        }

        // Populate currentYear
        output.push(this.yearReducer(currentYear));
        this.initialYear = this.yearReducer(currentYear);

        // Populate yearsForward if any
        if (yearsForward > 0) {
            for (let i = 1; i <= yearsForward; i++) {
                output.push(this.yearReducer(currentYear + i));
            }
        }

        // Autoselect current year
        if (this.selectSchoolYear) {
            let found = output.findIndex((year) => year === this.selectSchoolYear);

            if (found >= 0) {
                this.performanceSelect.setValue(output[found]);
                this.performancePeriodEvent.emit(output[found]);
            } else {
                output.push(this.selectSchoolYear);
                this.performanceSelect.setValue(this.selectSchoolYear);
                this.performancePeriodEvent.emit(this.selectSchoolYear);
            }
        } else {
            this.performanceSelect.setValue(this.yearReducer(currentYear));
            this.performancePeriodEvent.emit(this.yearReducer(currentYear));
        }

        output.sort((a, b) => b.localeCompare(a));

        return output;
    }

    populateSchoolYears(yearsBack: number, yearsForward: number): string[] {
        const today = new Date();
        const currentMonth: number = today.getMonth() + 1;
        let currentYear: number;

        if (this.startingSchoolYearDate) {
            if (today >= this.startingSchoolYearDate) {
                currentYear = this.startingSchoolYearDate.getFullYear() + this.currentYearModifier;
            } else {
                currentYear = this.startingSchoolYearDate.getFullYear() - 1 + this.currentYearModifier;
            }
        } else if (currentMonth >= 7) {
            currentYear = today.getFullYear() + this.currentYearModifier;
        } else {
            currentYear = today.getFullYear() - 1 + this.currentYearModifier;
        }

        if (this.minYear) {
            yearsBack = currentYear - this.minYear;
        }

        let output: string[] = [];

        // Populate yearsBack
        for (let i = yearsBack; i > 0; i--) {
            output.push(this.yearReducer(currentYear - i));
        }

        // Populate currentYear
        output.push(this.yearReducer(currentYear));
        this.initialYear = this.yearReducer(currentYear);

        // Populate yearsForward if any
        if (yearsForward > 0) {
            for (let i = 1; i <= yearsForward; i++) {
                output.push(this.yearReducer(currentYear + i));
            }
        }

        // Autoselect current year
        if (this.selectSchoolYear) {
            let found = output.findIndex((year) => year === this.selectSchoolYear);

            if (found >= 0) {
                this.performanceSelect.setValue(output[found]);
                this.performancePeriodEvent.emit(output[found]);
            } else {
                output.push(this.selectSchoolYear);
                this.performanceSelect.setValue(this.selectSchoolYear);
                this.performancePeriodEvent.emit(this.selectSchoolYear);
            }
        } else {
            this.performanceSelect.setValue(this.yearReducer(currentYear));
            this.performancePeriodEvent.emit(this.yearReducer(currentYear));
        }

        // if (this.currentYearModifier != -1) output.sort((a, b) => b.localeCompare(a));
        // else output.sort((a, b) => a.localeCompare(b));
        output.sort((a, b) => b.localeCompare(a));

        return output;
    }

    yearReducer(currentYear: number): string {
        let output = currentYear.toString() + '-' + this.nextYear(currentYear);

        return output;
    }

    nextYear(currentYear: number): string {
        let nextYearInt: number = currentYear + 1;

        return nextYearInt.toString().substring(2, 4);
    }

    selectInitialYear() {
        if (this.selectSchoolYear) {
            let found = this.years.findIndex((year) => year === this.selectSchoolYear);

            if (found >= 0) {
                this.performanceSelect.setValue(this.years[found]);
                this.performancePeriodEvent.emit(this.years[found]);
            } else {
                this.years.push(this.selectSchoolYear);
                this.performanceSelect.setValue(this.selectSchoolYear);
                this.performancePeriodEvent.emit(this.selectSchoolYear);
            }
        } else {
            this.performanceSelect.setValue(this.initialYear);
            this.performancePeriodEvent.emit(this.initialYear);
        }
        this.reset = false;
    }

    resetYears(performancePeriod: string) {
        this.initialYear = performancePeriod;
        console.log('calling resetyears');
        this.selectInitialYear();
    }
}
